<template>
  <b-card class="slack-settings">
    <img src="@src/assets/integrations/slack-logo.svg" width="200px"/>
    <b-row>
      <b-col md="8">
        <p>You can optionally enable Slack notifications for recruiters &amp; employees.</p>
        <ul>
          <li>Employees receive notifications as direct messages when candidates apply.</li>
          <li>
            Recruiters receive notifications in a private channel when candidates apply.<br/>
            <strong>We recommend you setup a new private channel for referral notifications, e.g. <b><em>#recruiters</em> or <em>#reallinks-recruiters</em></b>.</strong>
          </li>
          <li>Once connected, add Slack to your onboarding steps to encourage employees to connect their Slack account</li>
        </ul>
      </b-col>
      <b-col md="4">
        <template v-if="loading">
          <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
        </template>
        <template v-else-if="slack && slack.company && slack.company.team_id">
          <table class="table b-table b-table-stacked">
            <tbody>
              <tr>
                <td data-label="Status"><span class="text-success">Connected!</span></td>
                <td data-label="Team">{{ slack.company.team_name }}</td>
                <td data-label="Channel">{{ slack.company.webhook_channel }}</td>
                <td data-label="Connected By">
                  <div class="d-flex flex-row justify-content-start align-items-center">
                    <img class="rounded mr-1" :src="slack.company.user_avatar" height="25px" width="25px"/>
                    {{ slack.company.user_name }}
                  </div>
                </td>
                <td data-label="">
                  <b-btn class="d-flex flex-row justify-content-start align-items-center" @click="disconnectSlack" variant="slack">
                    <img src="@src/assets/integrations/slack-mark.svg" height="30px"/>
                    <strong>Disconnect</strong>
                  </b-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
        <b-btn v-else class="d-flex flex-row justify-content-start align-items-center" :href="redirectToSlack" variant="slack">
          <img src="@src/assets/integrations/slack-mark.svg" height="30px"/>
          <strong>Add to Slack</strong>
        </b-btn>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import axios from 'axios';
import base64 from 'base-64';
import gql from 'graphql-tag';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import { mapRoot } from '@/mixins/root';

// Keep these responses in-sync
const GetSlackStateQuery = gql`
  query GetSlackState {
    company {
      id
      state {
        slack
      }
    }
    user {
      id
      state {
        slack
      }
    }
  }
`;
const UpdateSlackStateMutation = gql`
  mutation SetSlackState($userID: ID!, $company: KeyValueHash, $user: KeyValueHash) {
    company: updateCompany(update: { state: { slack: $company } }) {
      id
      state {
        slack
      }
    }
    user: updateUser(update: { state: { slack: $user } }, where: { id: $userID }) {
      id
      state {
        slack
      }
    }
  }
`;

export default {
  name: 'SlackSettings',
  components: {
    LoadingSpinner,
  },
  data: () => ({
    loading: true,
  }),
  apollo: {
    slack: {
      query: GetSlackStateQuery,
      update: ({ company, user }) => ({
        company: ((company || {}).state || {}).slack || null,
        user: ((user || {}).state || {}).slack || null,
      }),
      result({ loading, data }) {
        if (!loading && data && data.company && !this.$route.hash) {
          this.loading = false;
        }
      }
    },
  },
  metaInfo: {
    title: 'Slack Settings',
  },
  async mounted() {
    const { hash } = this.$route;
    if (`${hash}`.length > 1) {
      const parsed = JSON.parse(base64.decode(hash.substr(1)));
      await this.parseResponse(parsed);
      this.$router.replace({ hash: '' });
    }
  },
  computed: {
    ...mapRoot([ 'user', 'origin', 'servicesOrigin' ]),
    redirectToSlack() {
      const returnUri = `${this.origin}${this.$route.path}`;

      // Previously, we also wanted to configure an incoming webhook for shared notifications
      // const scope = 'chat:write,chat:write.customize,incoming-webhook';

      const scope = 'chat:write,chat:write.customize';
      const userScope = 'identity.basic,identity.avatar';
      return `${this.servicesOrigin}/integrations/slack/auth?scope=${scope}&return_uri=${returnUri}&user_scope=${userScope}`;
    },
  },
  methods: {
    async parseResponse(res) {
      try {
        this.loading = true;
        this.$log.debug(res);

        this.$assert(res && res.ok, new Error(res.error));

        if (res.uninstalled) {
          return this.$notification({ message: 'Removed from Slack', variant: 'success' });
        }

        this.$assert(res.access_token, new Error('Missing bot access token'));
        this.$assert(res.team && res.team.id, new Error('Missing team ID'));
        this.$assert(res.incoming_webhook && res.incoming_webhook.url, new Error('Missing webhook url'));
        this.$assert(res.authed_user && res.authed_user.access_token, new Error('Missing user access token'));

        const { data: { user } } = await axios.get('https://slack.com/api/users.identity', {
          params: { token: res.authed_user.access_token },
          validateStatus: status => status === 200,
        });

        this.$log.debug({ user });

        const avatar = user.image_1024 || user.image_512 || user.image_256 || user.image_192 || user.image_72;

        const companyState = {
          app_id: res.app_id,
          team_id: res.team.id,
          team_name: res.team.name,
          bot_user_id: res.bot_user_id,
          bot_scope: res.scope,
          bot_token: res.access_token,
          user_id: res.authed_user.id,
          user_name: user.name,
          user_avatar: avatar,
          webhook_channel: res.incoming_webhook.channel,
          webhook_channel_id: res.incoming_webhook.channel_id,
          webhook_config_url: res.incoming_webhook.config_url,
          webhook_url: res.incoming_webhook.url,
        };
        const userState = {
          app_id: res.app_id,
          team_id: res.team.id,
          user_id: res.authed_user.id,
          user_name: user.name,
          user_avatar: avatar,
          user_scope: res.authed_user.scope,
          user_token: res.authed_user.access_token,
        };

        const { id: userID } = this.user;
        await this.$apollo.mutate({
          mutation: UpdateSlackStateMutation,
          variables: { userID, company: companyState, user: userState },
          update: (store, { data }) => store.writeQuery({ query: GetSlackStateQuery, data }),
        });
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to add to Slack' });
      } finally {
        this.loading = false;
      }
    },
    async disconnectSlack() {
      try {
        this.loading = true;
        const { bot_token: botToken } = this.slack.company;
        this.$assert(botToken, new Error('Missing bot_token to disconnect Slack'));

        const { id: userID } = this.user;
        await this.$apollo.mutate({
          mutation: UpdateSlackStateMutation,
          variables: { userID, company: null, user: null },
        });

        const returnUri = `${this.origin}${this.$route.path}`;
        window.location = `${this.servicesOrigin}/integrations/slack/auth?destroy=${botToken}&return_uri=${returnUri}`;
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to remove from Slack' });
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.slack-settings {
  .btn-group {
    .btn {
      min-width: 100px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/settings/Slack.vue